import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useT } from '@shared/i18n';

const OFFLINE_TOAST_ID = 'connectivity-offline';
const BACK_ONLINE_VISIBLE_MS = 3000;

export interface OfflineNoticeProps {
	isOnline: boolean;
	offlineMessage?: string;
	onlineMessage?: string;
	dismissLabel?: string;
}

/**
 * App-wide "you are offline" notice, driven by `sonner`'s imperative
 * `toast()` API against the shared `<Toaster />` mounted once in
 * `app/app.tsx` (`shared/ui/sonner.tsx`). Renders nothing itself -- it's a
 * pure bridge from the `isOnline` boolean to the toast queue, not a visual
 * component, since sonner owns the actual DOM/portal.
 *
 * Still mounted once in `app.tsx`, outside `<Template />`, so every one of
 * the ten templates gets it by construction.
 *
 * **Replaces a bare `window.alert('You are offline')`** that used to fire from
 * an effect in `app.tsx`, and before that, a hand-rolled Radix `Toast`. Same
 * requirement both times, four problems with `alert()`:
 *
 * 1. `alert()` is synchronous and blocking — it freezes rendering, the service
 *    worker message channel, and any in-flight retry until the reader
 *    dismisses it. Being offline is precisely when the app should stay
 *    responsive against its caches.
 * 2. It fired only on the *transition* into offline. Offline is a state, not
 *    an event: a reader who opens the app already offline, or who dismisses
 *    the dialog and keeps browsing, got no indication at all. This stays
 *    visible for as long as the condition holds.
 * 3. It never said when connectivity came back, so the reader had no way to
 *    know it was safe to retry.
 * 4. It's an unstyleable OS-level dialog with the origin in the title — in an
 *    installed white-label PWA that reads as a browser error, not the
 *    publisher's app.
 *
 * `id: OFFLINE_TOAST_ID` + `duration: Infinity` is what makes an imperative,
 * queue-shaped API represent a *state* rather than a one-shot message: calling
 * `toast.error()` again with the same id updates the existing toast in place
 * instead of stacking a second one, and nothing but `toast.dismiss(id)` (fired
 * the instant `isOnline` flips) or the reader's own close-button tap closes it
 * early. The "back online" toast is a real transient one with its own
 * duration, so it clears itself.
 *
 * `action` (a real "Dismiss" button), not sonner's built-in `closeButton`,
 * and `dismissible` stays `false`: swiping away a toast that represents an
 * ongoing state (rather than a one-shot message) reads as accidental on a
 * touch device, and doesn't work at all with a mouse, so the swipe gesture
 * stays off. `closeButton` would have been the obvious choice, but sonner
 * gates its click handler on `dismissible` internally (`onClick: disabled ||
 * !dismissible ? noop : dismiss`, verified against the installed package's
 * own source) -- with `dismissible: false` it renders a button that visibly
 * does nothing when tapped, which is worse than no button. `action`'s click
 * handler has no such gate, so it's the one sonner option that gives a real,
 * always-working tap target independent of the swipe setting. `isOnline`
 * flipping back still clears the toast automatically regardless of whether
 * the reader dismissed it manually first.
 */
export function OfflineNotice({
	isOnline,
	offlineMessage,
	onlineMessage,
	dismissLabel,
}: OfflineNoticeProps) {
	const t = useT();
	// Starting offline should show the offline toast but must not, on the
	// first connection, announce "back online" for a reader who was never
	// offline in this session.
	const wasOffline = useRef(!isOnline);

	useEffect(() => {
		if (!isOnline) {
			wasOffline.current = true;
			toast.error(offlineMessage ?? t('common.offline'), {
				id: OFFLINE_TOAST_ID,
				duration: Infinity,
				dismissible: false,
				// Empty on purpose: sonner dismisses the toast itself right
				// after this runs (unless the event is `preventDefault()`-ed,
				// which this doesn't do) -- there's nothing else to do here.
				action: {
					label: dismissLabel ?? t('common.dismiss'),
					onClick: () => {},
				},
			});
			return;
		}

		toast.dismiss(OFFLINE_TOAST_ID);

		if (!wasOffline.current) return;
		wasOffline.current = false;
		toast.success(onlineMessage ?? t('common.backOnline'), {
			duration: BACK_ONLINE_VISIBLE_MS,
		});
	}, [isOnline, offlineMessage, onlineMessage, dismissLabel, t]);

	return null;
}
